import { z } from 'zod';

/**
 * Validate an SSN string against strict SSN rules.
 * Based on Apex documentation: https://developer.apexclearing.com/apex-fintech-solutions/docs/account-data-validation
 *
 * Update Aug 3, 2026 - Apex is almost certainly wrong about '123' not being a valid start.
 *
 * Rules:
 * - First three digits must not be 666, 000
 * - Fourth and fifth digits must not be 00
 * - Last four digits must not be 0000
 * - Cannot have all digits the same value
 *
 * @param ssn - The SSN to validate (format: XXX-XX-XXXX or XXXXXXXXX)
 * @returns true if valid, false otherwise
 */
export const isValidSSN = (ssn: string): boolean => {
    // Remove hyphens for validation
    const digits = ssn.replace(/-/g, '');

    // Must be exactly 9 digits
    if (!/^\d{9}$/.test(digits)) {
        return false;
    }

    const firstThree = digits.substring(0, 3);
    const middleTwo = digits.substring(3, 5);
    const lastFour = digits.substring(5, 9);

    // First three digits must not be 666, 000
    if (firstThree === '666' || firstThree === '000') {
        return false;
    }

    // Fourth and fifth digits must not be 00
    if (middleTwo === '00') {
        return false;
    }

    // Last four digits must not be 0000
    if (lastFour === '0000') {
        return false;
    }

    // Cannot have all digits the same value
    const allSameDigit = /^(\d)\1{8}$/.test(digits);
    if (allSameDigit) {
        return false;
    }

    return true;
};

/**
 * Zod schema for validating SSN (Social Security Number).
 *
 * Format: XXX-XX-XXXX
 *
 * Validation rules:
 * - Must match format XXX-XX-XXXX
 * - First three digits must not be 666, 000, or 123
 * - Fourth and fifth digits must not be 00
 * - Last four digits must not be 0000
 * - Cannot have all digits the same value
 */
export const zodSsnValidation = z
    .string()
    .regex(/\d{3}-\d{2}-\d{4}/, 'Please enter a valid SSN')
    .refine((ssn) => isValidSSN(ssn), {
        message: 'Please enter a valid SSN',
    });
